import { UUIInputVariant, UUIInputSize, UUIInputStyle, UUIInputSkin, UUIInputTheme } from '../../../types/UUIInput'
import './UUIInput.module.scss'

const baseClassName = (size: UUIInputSize = 'normal'): string => {
  const base = [
    // common styles
    'tw:box-border tw:relative tw:rounded-[20px] tw:border tw:min-w-0 tw:w-full tw:font-ubuntu tw:outline-2 tw:outline-transparent tw:text-grey-50 tw:bg-grey-1000',
    //aria-disabled
    'tw:aria-disabled:shadow-none tw:aria-disabled:bg-grey-900 tw:aria-disabled:border-grey-900 tw:aria-disabled:cursor-default',
    // disabled
    'tw:has-[input:disabled]:shadow-none tw:has-[input:disabled]:bg-grey-900 tw:has-[input:disabled]:border-grey-900 tw:has-[input:disabled]:cursor-default',
    // read-only
    'tw:has-[input:read-only]:!shadow-none tw:has-[input:read-only]:!bg-grey-800 tw:has-[input:read-only]:!border-grey-800',
    // transitions
    'tw:transition-all tw:duration-300 tw:ease-in-out',
    // delete icon
    'tw:[&_.icon-aytmfd-x]:cursor-pointer',
    'tw:[&_.icon-aytmfd-x]:hover:text-grey-100'
  ].join(' ')

  let sizeClassName = ''
  switch (size) {
    case 'small': {
      sizeClassName = `tw:text-[12px]  tw:py-[3px] tw:leading-[16px] tw:font-normal tw:leading-4 tw:pr-3 tw:pl-4 tw:[&_.icon-aytmfd-x]:text-[12px] tw:[&_.icon-aytmfd-x]:relative `

      break
    }
    case 'large': {
      sizeClassName = `tw:text-[16px] tw:py-[7px] tw:leading-[24px] tw:font-normal tw:leading-6 tw:pr-3 tw:pl-4 tw:[&_.icon-aytmfd-x]:text-[16px] tw:[&_.icon-aytmfd-x]:relative `
      break
    }
    default:
    case 'normal': {
      sizeClassName = `tw:text-[14px] tw:leading-[20px] tw:py-[5px] tw:font-normal tw:leading-5 tw:pr-3 tw:pl-4 tw:[&_.icon-aytmfd-x]:text-[14px]   tw:[&_.icon-aytmfd-x]:relative `
      break
    }
  }
  return `${base} ${sizeClassName}`
}

// Field colours for the auth cards. The fill is dark rather than light: the card is a
// clear pane, so the field has to carry its own ground for the type to stay readable.
// Fill and border are separate: the invalid variant reuses the fill but must NOT carry
// the neutral border - two `!important` border rules of equal specificity are settled by
// source order, and the white one wins, so an unfocused invalid field looked valid.
const AUTH_FIELD_FILL = 'tw:!bg-[rgba(8,6,22,0.30)] tw:text-white'
const AUTH_FIELD_REST = `${AUTH_FIELD_FILL} tw:!border-white/60`
// A single ring, no second outline and no glow behind it: two outlines around one field
// read as a shadow rather than as focus. Written out in full - the Tailwind scanner
// reads literal class names, not template-built ones. The ring takes the iris accent
// rather than white: white is the resting edge too, so focus only thickened the line.
const AUTH_FOCUS_RING =
  'tw:focus-within:ring-2 tw:focus-within:ring-auth-icon tw:hover:focus-within:ring-2 tw:hover:focus-within:ring-auth-icon'
// Invalid keeps one colour on the outline: the ring moves into the error tone too, so a
// focused invalid field never shows a white ring wrapped around a red edge. The wording
// below the field carries the state for anyone who cannot separate the two hues.
const AUTH_FOCUS_RING_WARNING =
  'tw:focus-within:ring-2 tw:focus-within:ring-auth-warning tw:hover:focus-within:ring-2 tw:hover:focus-within:ring-auth-warning'
const AUTH_FOCUS_RING_ERROR =
  'tw:focus-within:ring-2 tw:focus-within:ring-auth-error tw:hover:focus-within:ring-2 tw:hover:focus-within:ring-auth-error'
// Disabled is solid, not translucent: opacity fades the border, the ring and every
// string at once, which drags the whole control below AA in a single step.
const AUTH_DISABLED =
  'tw:aria-disabled:!bg-auth-disabled-bg tw:aria-disabled:!border-auth-disabled-border tw:aria-disabled:!text-auth-disabled-text tw:has-[input:disabled]:!bg-auth-disabled-bg tw:has-[input:disabled]:!border-auth-disabled-border tw:has-[input:disabled]:!text-auth-disabled-text tw:has-[input:read-only]:!bg-white/5 tw:has-[input:read-only]:!border-white/35'

const styleClassName = (
  style: UUIInputStyle = 'filled',
  variant: UUIInputVariant = 'default',
  skin: UUIInputSkin = 'default'
): string => {
  let styleClassName = ''
  let variantClassName = ''
  if ('filled' === style) {
    styleClassName = [
      'tw:text-grey-50 tw:border-grey-1000 tw:shadow-[0px_1px_8px_0px_#737D8C26]',
      // hover
      'tw:hover:border-aquamarine-400 tw:hover:shadow-[0px_1px_5px_0px_#5BBFC059]',
      // focus-within
      'tw:focus-within:border-blue-100 tw:focus-within:shadow-[0px_0px_0px_2px_#29ABE259]',
      // hover + focus-within (override hover)
      'tw:hover:focus-within:border-blue-100 tw:hover:focus-within:shadow-[0px_0px_0px_2px_#29ABE259]'
    ].join(' ')
    switch (variant) {
      case 'default': {
        variantClassName = ['tw:bg-grey-1000'].join(' ')
        break
      }
      case 'invalid': {
        variantClassName = [
          // resting red border mirrors the outline style — without it an unfocused
          // invalid field reads as valid next to a focused one (OQ-LOG-56)
          'tw:border-red-50 tw:shadow-[0px_1px_8px_0px_#E2294E26]',
          // hover
          'tw:hover:border-red-100 tw:hover:shadow-[0px_1px_8px_0px_#E2294E26]',
          // focus-within
          'tw:focus-within:border-red-50 tw:focus-within:shadow-[0px_0px_0px_2px_#E2294E59] tw:focus-within:text-grey-50',
          // hover + focus-within (override hover)
          'tw:hover:focus-within:border-red-50 tw:hover:focus-within:shadow-[0px_0px_0px_2px_#E2294E59] tw:hover:focus-within:text-grey-50'
        ].join(' ')
        break
      }
      case 'warning': {
        variantClassName = [
          'tw:border-transparent tw:!shadow-[0px_0px_8px_0px_#f0953d26]',
          // hover
          'tw:hover:border-yellow-10 ',
          // focus-within
          'tw:focus-within:border-yellow-10 tw:focus-within:shadow-[0px_0px_0px_2px_#f0953d59]',
          // hover + focus-within (override hover)
          'tw:hover:focus-within:border-yellow-10 tw:hover:focus-within:shadow-[0px_0px_0px_2px_#f0953d59]'
        ].join(' ')
        break
      }
    }
  } else if ('outline' === style) {
    const baseOutlineStyles = [
      'tw:text-grey-50 tw:border-grey-700',
      // aria-disabled
      'tw:aria-disabled:bg-grey-800 tw:aria-disabled:border-grey-800 tw:aria-disabled:cursor-default',
      // disabled
      'tw:disabled:bg-grey-800 tw:disabled:border-grey-800',
      // read-only
      'tw:has-[input:read-only]:bg-grey-800 tw:has-[input:read-only]:border-grey-700'
    ].join(' ')

    switch (variant) {
      case 'default': {
        variantClassName = (
          skin === 'auth'
            ? [
                AUTH_FIELD_REST,
                // hover: the edge brightens, nothing else - a glow behind the field reads as a shadow
                'tw:hover:!border-white/85',
                // focus-within, including over hover - edge moves into the ring's tone
                'tw:focus-within:!border-auth-icon tw:hover:focus-within:!border-auth-icon',
                AUTH_FOCUS_RING,
                AUTH_DISABLED
              ]
            : skin === 'account'
              ? [
                  'tw:bg-grey-1000 tw:!border-aquamarine-100 tw:text-primary-text',
                  // hover
                  'tw:hover:!border-aquamarine-400',
                  // focus-within
                  'tw:focus-within:border-blue-100 tw:focus-within:shadow-[0px_0px_0px_3px_#29ABE259]',
                  // hover + focus-within (override hover)
                  'tw:hover:focus-within:border-blue-100 tw:hover:focus-within:shadow-[0px_0px_0px_3px_#29ABE259]'
                ]
              : [
                  'tw:bg-grey-1000',
                  // hover
                  'tw:hover:border-aquamarine-400',
                  // focus-within
                  'tw:focus-within:border-blue-100 tw:focus-within:shadow-[0px_0px_0px_3px_#29ABE259]',
                  // hover + focus-within (override hover)
                  'tw:hover:focus-within:border-blue-100 tw:hover:focus-within:shadow-[0px_0px_0px_3px_#29ABE259]'
                ]
        ).join(' ')
        break
      }
      case 'invalid': {
        variantClassName = (
          skin === 'auth'
            ? [
                // a 2px edge in the error tone; red-50 is too dark to read as an edge here
                `${AUTH_FIELD_FILL} tw:!border-2 tw:!border-auth-error`,
                // hover
                'tw:hover:!border-auth-error',
                // focus keeps the error tone right through the ring
                'tw:focus-within:!border-auth-error tw:hover:focus-within:!border-auth-error',
                AUTH_FOCUS_RING_ERROR,
                AUTH_DISABLED
              ]
            : [
                'tw:border-red-50',
                // hover
                'tw:hover:border-red-100',
                // focus-within
                'tw:focus-within:border-red-50 tw:focus-within:shadow-[0px_0px_0px_2px_#E2294E59] tw:focus-within:text-grey-50',
                // hover + focus-within (override hover)
                'tw:hover:focus-within:border-red-50 tw:hover:focus-within:shadow-[0px_0px_0px_2px_#E2294E59] tw:hover:focus-within:text-grey-50'
              ]
        ).join(' ')
        break
      }
      case 'warning': {
        variantClassName = (
          skin === 'auth'
            ? [
                // yellow-10 is the raw light-page amber; the card has its own
                `${AUTH_FIELD_FILL} tw:!border-2 tw:!border-auth-warning`,
                // hover
                'tw:hover:!border-auth-warning',
                // focus keeps the warning tone right through the ring, as invalid does
                'tw:focus-within:!border-auth-warning tw:hover:focus-within:!border-auth-warning',
                AUTH_FOCUS_RING_WARNING,
                AUTH_DISABLED
              ]
            : [
                'tw:border-yellow-10',
                // hover
                'tw:hover:border-yellow-10 tw:hover:shadow-none',
                // focus-within
                'tw:focus-within:border-yellow-10 tw:focus-within:shadow-[0px_0px_8px_0px_#f0953d59]',
                // hover + focus-within (override hover)
                'tw:hover:focus-within:border-yellow-10 tw:hover:focus-within:shadow-[0px_0px_8px_0px_#f0953d59]'
              ]
        ).join(' ')
        break
      }
    }

    styleClassName = baseOutlineStyles
  }
  return `${styleClassName} ${variantClassName}`
}
const styleInputClassName = (
  variant: UUIInputVariant = 'default',
  size: UUIInputSize = 'normal',
  skin: UUIInputSkin = 'default'
): string => {
  const base = [
    'tw:group-aria-disabled:pointer-events-none tw:flex-1 tw:min-w-0 tw:bg-transparent tw:border-none tw:outline-none tw:text-inherit tw:disabled:placeholder:text-grey-400 tw:disabled:cursor-default tw:has-[input:read-only]:cursor-default'
  ].join(' ')
  // grey-500 disappears on the dark field; the auth placeholder measures 10.5:1 at white/85
  const placeholder = skin === 'auth' ? 'tw:placeholder:text-white/85' : 'tw:placeholder:text-grey-500'
  // Chrome paints autofill on the input, not on the pill around it, so its opaque wash
  // landed inset in the field ignoring the rounded edge, carrying near-black type onto the
  // dark ground. Clipping that background to the glyphs hides it and lets the pill's own
  // translucent fill through — an inset shadow would need an opaque colour and flatten the
  // glass. Light skins keep the native wash: on white it reads as the expected "browser
  // filled this" cue.
  const autofill =
    skin === 'auth'
      ? 'tw:autofill:[-webkit-background-clip:text] tw:autofill:[-webkit-text-fill-color:white] tw:autofill:caret-white'
      : ''
  let variantClassName = ''
  switch (variant) {
    case 'default': {
      variantClassName = [placeholder, autofill].join(' ')
      break
    }
    case 'invalid': {
      variantClassName = [placeholder, autofill].join(' ')
      break
    }
    case 'warning': {
      variantClassName = [placeholder, autofill].join(' ')
      break
    }
  }
  let sizeClassName = ''
  switch (size) {
    case 'small': {
      sizeClassName = 'tw:placeholder:text-[12px] tw:text-[12px] tw:h-[16px]'
      break
    }
    case 'large': {
      sizeClassName = 'tw:placeholder:text-[16px] tw:text-[16px] tw:h-[24px]'
      break
    }
    case 'normal': {
      sizeClassName = 'tw:placeholder:text-[14px] tw:text-[14px] tw:h-[20px]'
      break
    }
  }

  return `${base} ${variantClassName} ${sizeClassName}`
}

const groupClassName = (size: UUIInputSize = 'normal', skin: UUIInputSkin = 'default'): string => {
  const base = [
    skin === 'auth'
      ? 'tw:disabled:cursor-default tw:group-aria-disabled:pointer-events-none tw:group-aria-disabled:cursor-default tw:text-white/85 tw:hover:text-white tw:transition-all tw:duration-300 tw:ease-in-out'
      : 'tw:disabled:cursor-default tw:group-aria-disabled:pointer-events-none tw:group-aria-disabled:cursor-default tw:text-grey-600 tw:hover:text-grey-300 tw:transition-all tw:duration-300 tw:ease-in-out'
  ]
  let sizeClass = ''
  switch (size) {
    case 'small':
      sizeClass = 'tw:text-[12px] tw:leading-[16px] '
      break
    case 'large':
      sizeClass = 'tw:text-[16px] tw:leading-[24px]'
      break
    case 'normal':
      sizeClass = 'tw:text-[14px] tw:leading-[20px]'
      break
  }
  return `${base.join(' ')} ${sizeClass}`
}

const inputTheme: UUIInputTheme = {
  element: ({ size, style, variant, skin }) =>
    `tw:flex tw:group ${baseClassName(size)} ${styleClassName(style, variant, skin)}`,
  input: ({ variant, size, skin }) => `${styleInputClassName(variant, size, skin)}`,
  leftIcon: ({ size, skin }) => `${groupClassName(size, skin)} tw:mr-2 `,
  rightIcon: ({ size, skin }) => `${groupClassName(size, skin)} tw:ml-2 tw:cursor-pointer `,
  counter: ({ size, skin }) =>
    `${groupClassName(size, skin)} tw:text-grey-500 tw:ml-2 tw:hover:text-grey-500 tw:!text-[10px]`,
  leftAddons: ({ size, skin }) => `${groupClassName(size, skin)} tw:mr-2 tw:flex tw:cursor-pointer `,
  rightAddons: ({ size, skin }) => `${groupClassName(size, skin)} tw:ml-2 tw:flex tw:cursor-pointer`
}

export default inputTheme
